import type { NavigateFunction } from 'react-router-dom';
import { roundsApi, type StartRoundResponse } from '../api/roundsApi';
import { voiceAgentApi } from '../api/voiceAgentApi';
import { DEFAULT_MOCK_INTERVIEW_TOKEN } from '../features/interview/constants';
import type { QuickLaunchRoundType } from './quickLaunchUtils';

export interface LaunchRoundParams {
  interviewId: string;
  interviewName?: string;
  roundNum: number;
  roundType: QuickLaunchRoundType;
  language?: string;
}

export interface LaunchRoundSessionResult {
  error?: string;
}

const navigateAfterStart = (
  navigate: NavigateFunction,
  roundType: QuickLaunchRoundType,
  data: StartRoundResponse,
  token: string
): void => {
  if (roundType === 'coding') {
    navigate('/coding-interview', {
      state: {
        sessionId: data.session_id,
        problemId: data.problem_id,
        language: data.language,
        mode: 'practice',
      },
    });
    return;
  }

  if (roundType === 'gd') {
    navigate('/group-discussion', {
      state: {
        sessionId: data.session_id,
        token,
      },
    });
    return;
  }

  navigate('/interview', {
    state: {
      sessionId: data.session_id,
      aiSessionId: data.ai_session_id,
      firstQuestion: data.first_question,
      audioUrl: data.audio_url,
      audioMode: data.audio_mode,
      maxQuestions: data.max_questions,
      geminiLive: data.gemini_live === true,
    },
  });
};

export const launchRoundSession = async (
  target: LaunchRoundParams,
  navigate: NavigateFunction,
  token: string = DEFAULT_MOCK_INTERVIEW_TOKEN
): Promise<LaunchRoundSessionResult> => {
  const useVoiceAgent =
    target.roundType === 'aptitude' && localStorage.getItem('use_voice_agent') === 'true';

  try {
    if (useVoiceAgent) {
      const data = await voiceAgentApi.startRound({
        customInterviewId: target.interviewId,
        roundNum: target.roundNum,
        token,
      });

      if (!data.success) {
        return { error: data.message || data.error || 'Failed to start round' };
      }

      if (!data.session_id || !data.ws_path) {
        return { error: 'Voice agent round started but session details were missing' };
      }

      navigate('/interview', {
        state: {
          voiceAgentBootstrap: {
            sessionId: data.session_id,
            wsPath: data.ws_path,
            firstQuestion: data.first_question,
            maxQuestions: data.max_questions,
            contextType: data.context_type ?? 'placement',
          },
        },
      });
      return {};
    }

    const { data } = await roundsApi.startRound(
      target.interviewId,
      target.roundNum,
      token,
      target.language
    );

    if (data.success === false && data.message) {
      return { error: data.message };
    }

    navigateAfterStart(navigate, target.roundType, data, token);
    return {};
  } catch (err: unknown) {
    const axiosErr = err as {
      message?: string;
      code?: string;
      response?: { data?: { detail?: string } };
    };
    const msg = axiosErr?.message || '';
    const isTimeout =
      axiosErr?.code === 'ECONNABORTED' || /timeout of \d+ms exceeded/i.test(msg);

    return {
      error: isTimeout
        ? 'Starting the round timed out. Please try again in a moment.'
        : axiosErr?.response?.data?.detail || 'Failed to start round',
    };
  }
};
